import {
  Badge,
  Button,
  Card,
  CardContent,
  cn,
  healthAttentionIconTileClass,
  healthAttentionSurfaceClass,
} from '@up2stay/up2promo-ui';
import { Plug } from 'lucide-react';

import { ShellLink } from '@/core/components/navigation/shell-link';
import {
  REPUTATION_SECTION_MUTED_CLASS,
  REPUTATION_SECTION_TITLE_CLASS,
} from '@/core/components/reputation/reputation-surface';
import type { ReputationConnectionSetupBannerVariant } from '@/core/hooks/use-reputation-connection-setup';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

export function ReputationConnectionSetupBanner({
  variant,
  needsActionCount,
}: {
  variant: ReputationConnectionSetupBannerVariant;
  needsActionCount: number;
}) {
  const dict = useDictionary();
  const d = dict.reputation?.connectionSetup as
    | {
        badge?: string;
        title?: string;
        description?: string;
        cta?: string;
        connectGoogleTitle?: string;
        connectGoogleDescription?: string;
        connectGoogleCta?: string;
        reconnectGoogleTitle?: string;
        reconnectGoogleDescription?: string;
        reconnectGoogleCta?: string;
      }
    | undefined;

  const title =
    variant === 'connect_google'
      ? d?.connectGoogleTitle
      : variant === 'reconnect_google'
        ? d?.reconnectGoogleTitle
        : d?.title?.replace('{count}', String(needsActionCount));

  const description =
    variant === 'connect_google'
      ? d?.connectGoogleDescription
      : variant === 'reconnect_google'
        ? d?.reconnectGoogleDescription
        : d?.description;

  const cta =
    variant === 'connect_google'
      ? d?.connectGoogleCta
      : variant === 'reconnect_google'
        ? d?.reconnectGoogleCta
        : d?.cta;

  const ctaHref =
    variant === 'connect_google' || variant === 'reconnect_google'
      ? shellPaths.moreWorkspaceIntegrations
      : `${shellPaths.reputationSetup}?tab=connections`;

  return (
    <Card
      className={cn('overflow-hidden shadow-none', healthAttentionSurfaceClass)}
    >
      <CardContent className="flex items-start gap-3 p-4 sm:p-5">
        <div
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--r-control)]',
            healthAttentionIconTileClass,
          )}
        >
          <Plug className="h-4 w-4" aria-hidden />
        </div>
        <div className="min-w-0 flex-1 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="warning" className="font-normal">
              {d?.badge}
            </Badge>
          </div>
          {title ? (
            <p className={REPUTATION_SECTION_TITLE_CLASS}>{title}</p>
          ) : null}
          {description ? (
            <p className={REPUTATION_SECTION_MUTED_CLASS}>{description}</p>
          ) : null}
          {cta ? (
            <div className="pt-0.5">
              <Button
                variant="outline"
                size="md"
                className="w-full sm:w-auto"
                asChild
              >
                <ShellLink to={ctaHref}>{cta}</ShellLink>
              </Button>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
